package com.islamicmedia.shared.domain.model

import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

/**
 * The member's own referral answer, kept on the device so the "Free Gift" screen opens instantly
 * instead of waiting on GET /api/referral/me every time. The code and link never change for an
 * account (the code is minted once per email; the link is derived from it), so the saved copy is
 * shown straight away and the counts are refreshed behind it.
 *
 * The saved copy is stamped with its [owner] — the account's email AND the backend (DEV and PROD
 * have different codes and different share links, and dev builds can switch between them) — and is
 * ignored for anyone else. Only an "ok" answer is ever saved.
 */
internal object ReferralInfoCache {
    private val json = Json { ignoreUnknownKeys = true; encodeDefaults = true }

    @Serializable
    private data class Entry(val owner: String, val info: ReferralInfo)

    /** "email|ENV", or null when there's no signed-in email to key it by. */
    fun owner(email: String?, environment: String): String? =
        email?.trim()?.lowercase()?.takeIf { it.isNotEmpty() }?.let { "$it|$environment" }

    /** What to store, or null when [info] isn't worth keeping (anything but a usable "ok"). */
    fun encode(owner: String, info: ReferralInfo): String? =
        if (!info.isOk) null else runCatching { json.encodeToString(Entry.serializer(), Entry(owner, info)) }.getOrNull()

    /** The saved answer, only if it belongs to [owner] and is still a usable "ok". */
    fun decode(raw: String?, owner: String): ReferralInfo? {
        if (raw.isNullOrBlank()) return null
        val entry = runCatching { json.decodeFromString(Entry.serializer(), raw) }.getOrNull() ?: return null
        return entry.info.takeIf { entry.owner == owner && it.isOk }
    }
}
